// src/firebase/auctionService.js
import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';

const USERS_COLLECTION = 'users';
const AUCTIONS_COLLECTION = 'auctions';
const PURCHASES_COLLECTION = 'purchases';
const PLAYER_NOTES_COLLECTION = 'playerNotes';
const PLAYER_BACKUP_GK_COLLECTION = 'playerBackupGoalkeepers';
const PMA_STATS_COLLECTION = 'pmaStats';
const WATCHED_AUCTIONS_COLLECTION = 'watchedAuctions';
const PUBLIC_AUCTIONS_COLLECTION = 'publicAuctions';

// ID doc in pmaStats: {uid}_{auctionId}_{playerId}
function pmaStatDocId(uid, auctionId, playerId) {
  return `${uid}_${auctionId}_${String(playerId)}`;
}

export const DEFAULT_ROSTER_LIMITS = {
  P: 3,
  D: 8,
  C: 8,
  A: 6,
};

const MANTRA_ROLE_CANONICAL_MAP = {
  P: 'P',
  POR: 'P',
  D: 'D',
  DS: 'Ds',
  DC: 'Dc',
  DD: 'Dd',
  B: 'B',
  E: 'E',
  M: 'M',
  C: 'C',
  W: 'W',
  T: 'T',
  A: 'A',
  PC: 'Pc',
};

const MANTRA_ROLE_ORDER = ['P', 'D', 'Ds', 'Dc', 'Dd', 'B', 'E', 'M', 'C', 'W', 'T', 'A', 'Pc'];

const PLAYER_CATEGORIES_COLLECTION = 'playerCategories';

const VALID_PLAYER_CATEGORIES = new Set([
  'none',
  'trap',
  'target',
  'injury',
  'internationalcup',
  'cheap',
  'key',
  'wager',
  'goodstarter',
  'custom',
]);

function auctionBasePath(uid) {
  return collection(db, USERS_COLLECTION, uid, AUCTIONS_COLLECTION);
}

function auctionDocPath(uid, auctionId) {
  return doc(db, USERS_COLLECTION, uid, AUCTIONS_COLLECTION, auctionId);
}

function purchasesCollectionPath(uid, auctionId) {
  return collection(db, USERS_COLLECTION, uid, AUCTIONS_COLLECTION, auctionId, PURCHASES_COLLECTION);
}

export function normalizeRosterLimits(limits = {}) {
  return {
    P: Number(limits.P ?? DEFAULT_ROSTER_LIMITS.P),
    D: Number(limits.D ?? DEFAULT_ROSTER_LIMITS.D),
    C: Number(limits.C ?? DEFAULT_ROSTER_LIMITS.C),
    A: Number(limits.A ?? DEFAULT_ROSTER_LIMITS.A),
    M: Number(limits.M ?? 0),
  };
}

// Limite minimo per ruolo: quanti giocatori di quel ruolo la rosa DEVE
// ancora contenere. Se non specificato, di default coincide col massimo
// (comportamento storico: rosa da riempire esattamente fino al limite).
// Sempre clampato tra 0 e il rispettivo massimo, qualunque sia il dato letto.
export function normalizeRosterMinLimits(minLimits = {}, maxLimits = DEFAULT_ROSTER_LIMITS) {
  const max = maxLimits || DEFAULT_ROSTER_LIMITS;

  const clampRole = (role) => {
    const maxVal = Math.max(0, Number(max?.[role] ?? 0));
    const raw = minLimits?.[role];
    const minVal = raw === undefined || raw === null || raw === '' ? maxVal : Number(raw);

    if (!Number.isFinite(minVal)) return maxVal;
    return Math.max(0, Math.min(minVal, maxVal));
  };

  return {
    P: clampRole('P'),
    D: clampRole('D'),
    C: clampRole('C'),
    A: clampRole('A'),
    M: clampRole('M'),
  };
}

function normalizeText(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function normalizeMantraRoleToken(token = '') {
  const cleaned = String(token).trim().replace(/\s+/g, '');
  if (!cleaned) return null;

  const key = cleaned.toUpperCase();
  return MANTRA_ROLE_CANONICAL_MAP[key] || null;
}

export function normalizeMantraRoleString(value = '') {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const tokens = raw
    .split(/[\/,|+]/g)
    .flatMap((part) => part.split(/\s+/g))
    .map((part) => part.trim())
    .filter(Boolean)
    .map(normalizeMantraRoleToken)
    .filter(Boolean);

  if (!tokens.length) return '';

  const unique = [];
  tokens.forEach((token) => {
    if (!unique.includes(token)) unique.push(token);
  });

  unique.sort((a, b) => MANTRA_ROLE_ORDER.indexOf(a) - MANTRA_ROLE_ORDER.indexOf(b));
  return unique.join('/');
}

export function normalizeRoleOverridesInput(input = {}) {
  const rows = Array.isArray(input)
    ? input
    : Object.entries(input).map(([name, roles]) => ({ name, roles }));

  return rows.reduce((acc, row) => {
    const name = String(row?.name || '').trim();
    const roles = row?.roles ?? row?.role ?? '';

    if (!name) return acc;

    const normalizedRoles = Array.isArray(roles)
      ? normalizeMantraRoleString(roles.join('/'))
      : normalizeMantraRoleString(String(roles || ''));

    if (!normalizedRoles) return acc;

    acc[name] = normalizedRoles;
    return acc;
  }, {});
}

function roleOverridesSignature(raw = {}) {
  const normalized = normalizeRoleOverridesInput(raw);
  const sortedEntries = Object.entries(normalized).sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(sortedEntries);
}

export function getAuctionRolesMode(auction) {
  const explicit = String(auction?.rolesMode || auction?.roleSystem || '').trim().toLowerCase();
  if (explicit === 'classic' || explicit === 'mantra') return explicit;

  // Fallback affidabile: se esiste un limite M > 0 è Mantra, altrimenti Classic.
  // Non usiamo playersSource perché FantApp può avere sia Classic che Mantra.
  if (Number(auction?.rosterLimits?.M) > 0 || Number(auction?.mantraMovementLimit) > 0) return 'mantra';

  return 'classic';
}

// Asta a listone: conta solo la propria rosa (niente Market/Squadre/Svincolati),
// si compone la squadra da un listone e si analizza. Il rolesMode resta
// classic | mantra come per un'asta normale.
export function isListoneAuction(auction) {
  return Boolean(auction?.listone);
}

// Slug della lega Fantacalcio.it, come compare nell'URL
// (leghe.fantacalcio.it/<slug>/...): minuscolo, solo [a-z0-9-].
export function sanitizeLeagueSlug(value = '') {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\/[^/]*\//, '')
    .replace(/\/.*$/, '')
    .replace(/[^a-z0-9-]/g, '');
}

function sameAuctionConfig(candidate, currentAuction) {
  if (!candidate || !currentAuction) return false;

  const candidateLimits = normalizeRosterLimits(candidate.rosterLimits || {});
  const currentLimits = normalizeRosterLimits(currentAuction.rosterLimits || {});

  const candidateMode = getAuctionRolesMode(candidate);
  const currentMode = getAuctionRolesMode(currentAuction);

  const baseMatch =
    candidateMode === currentMode &&
    Number(candidate.participantsCount || 0) === Number(currentAuction.participantsCount || 0) &&
    Number(candidate.credits || 0) === Number(currentAuction.credits || 0) &&
    candidateLimits.P === currentLimits.P &&
    candidateLimits.D === currentLimits.D &&
    candidateLimits.C === currentLimits.C &&
    candidateLimits.A === currentLimits.A &&
    candidateLimits.M === currentLimits.M;

  if (!baseMatch) return false;

  if (currentMode !== 'mantra') {
    return true;
  }

  return (
    roleOverridesSignature(candidate.roleOverrides || {}) ===
    roleOverridesSignature(currentAuction.roleOverrides || {})
  );
}

export function normalizePlayerCategory(category = 'none') {
  const value = String(category || 'none').trim().toLowerCase();
  return VALID_PLAYER_CATEGORIES.has(value) ? value : 'none';
}

function playerCategoriesCollectionPath(uid, auctionId) {
  return collection(db, USERS_COLLECTION, uid, AUCTIONS_COLLECTION, auctionId, PLAYER_CATEGORIES_COLLECTION);
}

function playerCategoryDocPath(uid, auctionId, playerId) {
  return doc(db, USERS_COLLECTION, uid, AUCTIONS_COLLECTION, auctionId, PLAYER_CATEGORIES_COLLECTION, String(playerId));
}

function playerNotesCollectionPath(uid, auctionId) {
  return collection(db, USERS_COLLECTION, uid, AUCTIONS_COLLECTION, auctionId, PLAYER_NOTES_COLLECTION);
}

function playerNoteDocPath(uid, auctionId, playerId) {
  return doc(db, USERS_COLLECTION, uid, AUCTIONS_COLLECTION, auctionId, PLAYER_NOTES_COLLECTION, String(playerId));
}

export async function getAuctionPlayerCategories(uid, auctionId) {
  if (!uid || !auctionId) return {};

  const snap = await getDocs(playerCategoriesCollectionPath(uid, auctionId));
  const result = {};

  snap.docs.forEach((d) => {
    const data = d.data() || {};
    const category = normalizePlayerCategory(data.category);

    if (category !== 'none') {
      result[String(data.playerId || d.id)] = category;
    }
  });

  return result;
}

export async function getAuctionPlayerNotes(uid, auctionId) {
  if (!uid || !auctionId) return {};

  const snap = await getDocs(playerNotesCollectionPath(uid, auctionId));
  const result = {};

  snap.docs.forEach((d) => {
    const data = d.data() || {};
    const note = String(data.notes || '').trim();

    if (note) {
      result[String(data.playerId || d.id)] = note;
    }
  });

  return result;
}

function playerBackupGoalkeeperCollectionPath(uid, auctionId) {
  return collection(db, USERS_COLLECTION, uid, AUCTIONS_COLLECTION, auctionId, PLAYER_BACKUP_GK_COLLECTION);
}

function playerBackupGoalkeeperDocPath(uid, auctionId, playerId) {
  return doc(db, USERS_COLLECTION, uid, AUCTIONS_COLLECTION, auctionId, PLAYER_BACKUP_GK_COLLECTION, String(playerId));
}

// Portiere di riserva della stessa squadra, indicato solo per i portieri
// (vedi StrategyEditPage.js colonna "Por riserva"). Nome libero, come le
// note — non un id giocatore.
export async function getAuctionPlayerBackupGoalkeepers(uid, auctionId) {
  if (!uid || !auctionId) return {};

  const snap = await getDocs(playerBackupGoalkeeperCollectionPath(uid, auctionId));
  const result = {};

  snap.docs.forEach((d) => {
    const data = d.data() || {};
    const name = String(data.backupName || '').trim();

    if (name) {
      result[String(data.playerId || d.id)] = name;
    }
  });

  return result;
}

export async function saveAuctionPlayerBackupGoalkeeper(uid, auctionId, playerId, backupName) {
  if (!uid || !auctionId || !playerId) return false;

  const normalized = String(backupName || '').trim();
  const ref = playerBackupGoalkeeperDocPath(uid, auctionId, playerId);

  if (!normalized) {
    await deleteDoc(ref);
    return true;
  }

  await setDoc(
    ref,
    { playerId: String(playerId), backupName: normalized, updatedAt: Date.now() },
    { merge: true }
  );

  return true;
}

export async function saveAuctionPlayerCategory(uid, auctionId, playerId, category) {
  if (!uid || !auctionId || !playerId) return false;

  const normalized = normalizePlayerCategory(category);
  const ref = playerCategoryDocPath(uid, auctionId, playerId);

  if (normalized === 'none') {
    await deleteDoc(ref);
    return true;
  }

  await setDoc(
    ref,
    {
      playerId: String(playerId),
      category: normalized,
      updatedAt: Date.now(),
    },
    { merge: true }
  );

  return true;
}

export async function deleteAuctionPlayerCategory(uid, auctionId, playerId) {
  if (!uid || !auctionId || !playerId) return false;
  await deleteDoc(playerCategoryDocPath(uid, auctionId, playerId));
  return true;
}

const PLAYER_TIERS_COLLECTION = 'playerTiers';

// Fascia di prezzo assegnata manualmente dall'operatore: 1 (top) .. 5 (più
// economica). 0 = nessuna fascia assegnata (equivalente a "none" per le categorie).
export function normalizePlayerTier(tier = 0) {
  const n = Math.round(Number(tier));
  return Number.isFinite(n) && n >= 1 && n <= 5 ? n : 0;
}

function playerTiersCollectionPath(uid, auctionId) {
  return collection(db, USERS_COLLECTION, uid, AUCTIONS_COLLECTION, auctionId, PLAYER_TIERS_COLLECTION);
}

function playerTierDocPath(uid, auctionId, playerId) {
  return doc(db, USERS_COLLECTION, uid, AUCTIONS_COLLECTION, auctionId, PLAYER_TIERS_COLLECTION, String(playerId));
}

export async function getAuctionPlayerTiers(uid, auctionId) {
  if (!uid || !auctionId) return {};

  const snap = await getDocs(playerTiersCollectionPath(uid, auctionId));
  const result = {};

  snap.docs.forEach((d) => {
    const data = d.data() || {};
    const tier = normalizePlayerTier(data.tier);

    if (tier > 0) {
      result[String(data.playerId || d.id)] = tier;
    }
  });

  return result;
}

export async function saveAuctionPlayerTier(uid, auctionId, playerId, tier) {
  if (!uid || !auctionId || !playerId) return false;

  const normalized = normalizePlayerTier(tier);
  const ref = playerTierDocPath(uid, auctionId, playerId);

  if (normalized === 0) {
    await deleteDoc(ref);
    return true;
  }

  await setDoc(
    ref,
    {
      playerId: String(playerId),
      tier: normalized,
      updatedAt: Date.now(),
    },
    { merge: true }
  );

  return true;
}

const PLAYER_MAX_SPEND_COLLECTION = 'playerMaxSpend';

// Tetto di spesa personale per un giocatore, in crediti ASSOLUTI per QUESTA
// asta (calcolato una volta sola all'import da una % di strategia — vedi
// importStrategyIntoAuction — oppure impostato a mano). 0/assente = nessun
// tetto impostato.
export function normalizePlayerMaxSpend(value = 0) {
  const n = Math.round(Number(value || 0));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function playerMaxSpendCollectionPath(uid, auctionId) {
  return collection(db, USERS_COLLECTION, uid, AUCTIONS_COLLECTION, auctionId, PLAYER_MAX_SPEND_COLLECTION);
}

function playerMaxSpendDocPath(uid, auctionId, playerId) {
  return doc(db, USERS_COLLECTION, uid, AUCTIONS_COLLECTION, auctionId, PLAYER_MAX_SPEND_COLLECTION, String(playerId));
}

export async function getAuctionPlayerMaxSpend(uid, auctionId) {
  if (!uid || !auctionId) return {};

  const snap = await getDocs(playerMaxSpendCollectionPath(uid, auctionId));
  const result = {};

  snap.docs.forEach((d) => {
    const data = d.data() || {};
    const value = normalizePlayerMaxSpend(data.credits);

    if (value > 0) {
      result[String(data.playerId || d.id)] = value;
    }
  });

  return result;
}

export async function saveAuctionPlayerMaxSpend(uid, auctionId, playerId, credits) {
  if (!uid || !auctionId || !playerId) return false;

  const normalized = normalizePlayerMaxSpend(credits);
  const ref = playerMaxSpendDocPath(uid, auctionId, playerId);

  if (normalized === 0) {
    await deleteDoc(ref);
    return true;
  }

  await setDoc(
    ref,
    {
      playerId: String(playerId),
      credits: normalized,
      updatedAt: Date.now(),
    },
    { merge: true }
  );

  return true;
}

// Svuota l'asta da tutti i dati provenienti da una strategia importata:
// cancella ogni documento nelle sottocollezioni playerCategories / playerTiers
// / playerNotes / playerMaxSpend / playerBackupGoalkeepers. Usato dal pulsante
// "Reset strategia" e come primo passo di importStrategyIntoAuction, così una
// nuova strategia non lascia refusi della precedente.
export async function clearAuctionStrategy(uid, auctionId) {
  if (!uid || !auctionId) return false;

  const collections = [
    playerCategoriesCollectionPath(uid, auctionId),
    playerTiersCollectionPath(uid, auctionId),
    playerNotesCollectionPath(uid, auctionId),
    playerMaxSpendCollectionPath(uid, auctionId),
    playerBackupGoalkeeperCollectionPath(uid, auctionId),
  ];

  const snaps = await Promise.all(collections.map((c) => getDocs(c)));
  const refs = snaps.flatMap((snap) => snap.docs.map((d) => d.ref));

  const CHUNK_SIZE = 400;
  for (let i = 0; i < refs.length; i += CHUNK_SIZE) {
    const batch = writeBatch(db);
    refs.slice(i, i + CHUNK_SIZE).forEach((ref) => batch.delete(ref));
    await batch.commit();
  }

  return true;
}

// Applica una strategia (etichette/fasce/note salvate a livello account) ai
// giocatori di un'asta. Prima di applicare, l'asta viene ripulita da ogni dato
// di strategia precedente (clearAuctionStrategy), così restano solo le
// informazioni della nuova strategia importata, senza refusi. L'operazione è
// una copia one-shot: le modifiche fatte dopo, dentro l'asta, non tornano mai
// nella strategia.
export async function importStrategyIntoAuction(uid, auctionId, strategy, auction) {
  if (!uid || !auctionId || !strategy) return { categories: {}, tiers: {}, notes: {}, maxSpends: {}, backupGoalkeepers: {} };

  // Rimuove ogni dato della strategia precedente prima di applicare la nuova,
  // così l'asta contiene solo le informazioni di quest'ultima (nessun refuso).
  await clearAuctionStrategy(uid, auctionId);

  const categories = strategy.categories || {};
  const tiers = strategy.tiers || {};
  const notes = strategy.notes || {};
  const maxSpendPercents = strategy.maxSpends || {};
  const backupGoalkeepers = strategy.backupGoalkeepers || {};

  // Il tetto di spesa è salvato nella strategia come % del budget totale
  // (portabile tra aste con crediti diversi): va tradotto in crediti
  // assoluti usando il budget di QUESTA asta, una volta sola qui.
  const auctionCredits = Math.max(Number(auction?.credits || 0), 0);
  const maxSpends = {};
  Object.entries(maxSpendPercents).forEach(([playerId, percent]) => {
    const value = normalizePlayerMaxSpend((Number(percent) || 0) * auctionCredits / 100);
    if (value > 0) maxSpends[playerId] = value;
  });

  const playerIds = Array.from(new Set([
    ...Object.keys(categories),
    ...Object.keys(tiers),
    ...Object.keys(notes),
    ...Object.keys(maxSpends),
    ...Object.keys(backupGoalkeepers),
  ]));

  const CHUNK_SIZE = 400;
  for (let i = 0; i < playerIds.length; i += CHUNK_SIZE) {
    const batch = writeBatch(db);

    playerIds.slice(i, i + CHUNK_SIZE).forEach((playerId) => {
      const category = normalizePlayerCategory(categories[playerId]);
      if (category !== 'none') {
        batch.set(
          playerCategoryDocPath(uid, auctionId, playerId),
          { playerId: String(playerId), category, updatedAt: Date.now() },
          { merge: true }
        );
      }

      const tier = normalizePlayerTier(tiers[playerId]);
      if (tier > 0) {
        batch.set(
          playerTierDocPath(uid, auctionId, playerId),
          { playerId: String(playerId), tier, updatedAt: Date.now() },
          { merge: true }
        );
      }

      const note = String(notes[playerId] || '').trim();
      if (note) {
        batch.set(
          playerNoteDocPath(uid, auctionId, playerId),
          { playerId: String(playerId), notes: note, updatedAt: Date.now() },
          { merge: true }
        );
      }

      const maxSpend = normalizePlayerMaxSpend(maxSpends[playerId]);
      if (maxSpend > 0) {
        batch.set(
          playerMaxSpendDocPath(uid, auctionId, playerId),
          { playerId: String(playerId), credits: maxSpend, updatedAt: Date.now() },
          { merge: true }
        );
      }

      const backupName = String(backupGoalkeepers[playerId] || '').trim();
      if (backupName) {
        batch.set(
          playerBackupGoalkeeperDocPath(uid, auctionId, playerId),
          { playerId: String(playerId), backupName, updatedAt: Date.now() },
          { merge: true }
        );
      }
    });

    await batch.commit();
  }

  return { categories, tiers, notes, maxSpends, backupGoalkeepers };
}

export async function getUserAuctions(uid) {
  const q = query(auctionBasePath(uid), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getPublicAuctions() {
  const snap = await getDocs(collection(db, PUBLIC_AUCTIONS_COLLECTION));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function syncPublicAuction(uid, auctionId, auctionData) {
  const ref = doc(db, PUBLIC_AUCTIONS_COLLECTION, auctionId);
  const rolesMode = getAuctionRolesMode(auctionData);
  const movementLimit = getAuctionMantraMovementLimit(auctionData);
  const normalizedLimits = {
    ...normalizeRosterLimits(auctionData.rosterLimits || {}),
    M: movementLimit,
  };
  await setDoc(ref, {
    ownerUid: uid,
    auctionId,
    name: auctionData.name || '',
    participantsCount: Number(auctionData.participantsCount || 0),
    credits: Number(auctionData.credits || 0),
    rolesMode,
    rosterLimits: normalizedLimits,
    mantraMovementLimit: rolesMode === 'mantra' ? movementLimit : null,
    playersSource: auctionData.playersSource || 'FantApp',
    myTeamName: auctionData.myTeamName || '',
    visibility: 'public',
    updatedAt: Date.now(),
  });
}

export async function removePublicAuction(uid, auctionId) {
  try {
    await deleteDoc(doc(db, PUBLIC_AUCTIONS_COLLECTION, auctionId));
  } catch {
    // Ignora se non esiste
  }
}

export async function addWatchedAuction(uid, ownerUid, auctionId) {
  const ref = doc(db, USERS_COLLECTION, uid, WATCHED_AUCTIONS_COLLECTION, auctionId);
  await setDoc(ref, { ownerUid, auctionId, addedAt: Date.now() });
  return true;
}

export async function removeWatchedAuction(uid, auctionId) {
  await deleteDoc(doc(db, USERS_COLLECTION, uid, WATCHED_AUCTIONS_COLLECTION, auctionId));
  return true;
}

export async function getUserWatchedAuctions(uid) {
  if (!uid) return [];
  const snap = await getDocs(collection(db, USERS_COLLECTION, uid, WATCHED_AUCTIONS_COLLECTION));
  const refs = snap.docs.map((d) => d.data());

  const results = await Promise.all(
    refs.map(async ({ ownerUid, auctionId }) => {
      try {
        const auction = await getAuctionById(ownerUid, auctionId);
        if (!auction || auction.visibility !== 'public') {
          // Riferimento stale: rimuovi silenziosamente
          deleteDoc(doc(db, USERS_COLLECTION, uid, WATCHED_AUCTIONS_COLLECTION, auctionId)).catch(() => {});
          return null;
        }
        return { ...auction, isWatched: true, watchedOwnerUid: ownerUid };
      } catch {
        return null;
      }
    })
  );

  return results.filter(Boolean);
}

export function getAuctionMantraMovementLimit(auction = {}) {
  const fromTopLevel = Number(auction?.mantraMovementLimit);
  if (Number.isFinite(fromTopLevel) && fromTopLevel > 0) return fromTopLevel;

  const fromRosterLimits = Number(auction?.rosterLimits?.M);
  if (Number.isFinite(fromRosterLimits) && fromRosterLimits > 0) return fromRosterLimits;

  const fallback =
    Number(auction?.rosterLimits?.D || 0) +
    Number(auction?.rosterLimits?.C || 0) +
    Number(auction?.rosterLimits?.A || 0);

  return fallback > 0 ? fallback : 0;
}

// Come getAuctionMantraMovementLimit ma per il minimo: se l'asta non ha un
// minimo salvato (aste create prima di questa feature), il fallback è il
// massimo stesso, preservando il comportamento storico (rosa da riempire
// esattamente).
export function getAuctionMantraMovementMinLimit(auction = {}) {
  const fromTopLevel = Number(auction?.mantraMovementMinLimit);
  if (Number.isFinite(fromTopLevel) && fromTopLevel > 0) return fromTopLevel;

  const fromRosterMinLimits = Number(auction?.rosterMinLimits?.M);
  if (Number.isFinite(fromRosterMinLimits) && fromRosterMinLimits > 0) return fromRosterMinLimits;

  return getAuctionMantraMovementLimit(auction);
}

export async function saveAuctionPlayerNote(uid, auctionId, playerId, notes) {
  if (!uid || !auctionId || !playerId) return false;

  const normalized = String(notes || '').trim();
  const ref = playerNoteDocPath(uid, auctionId, playerId);

  if (!normalized) {
    await deleteDoc(ref);
    return true;
  }

  await setDoc(
    ref,
    {
      playerId: String(playerId),
      notes: normalized,
      updatedAt: Date.now(),
    },
    { merge: true }
  );

  return true;
}

export async function createAuction(uid, payload) {
  const rolesMode = getAuctionRolesMode({
    rolesMode: payload.rolesMode ?? payload.roleSystem,
    playersSource: payload.playersSource,
  });

  const normalizedRosterLimits = normalizeRosterLimits(payload.rosterLimits);
  const mantraMovementLimit = Number(
    payload.mantraMovementLimit ??
    normalizedRosterLimits.M ??
    0
  );

  const normalizedRosterMinLimits = normalizeRosterMinLimits(payload.rosterMinLimits, normalizedRosterLimits);
  const mantraMovementMinLimitRaw = Number(
    payload.mantraMovementMinLimit ??
    normalizedRosterMinLimits.M ??
    mantraMovementLimit
  );
  const mantraMovementMinLimit = Number.isFinite(mantraMovementMinLimitRaw)
    ? Math.max(0, Math.min(mantraMovementMinLimitRaw, mantraMovementLimit))
    : mantraMovementLimit;

  const auctionPayload = {
    ...payload,
    rolesMode,
    playersSource: String(payload.playersSource || 'FantApp'),
    roleOverrides: normalizeRoleOverridesInput(payload.roleOverrides),
    rosterLimits: normalizedRosterLimits,
    rosterMinLimits: normalizedRosterMinLimits,
    mantraMovementLimit: rolesMode === 'mantra' ? mantraMovementLimit : null,
    mantraMovementMinLimit: rolesMode === 'mantra' ? mantraMovementMinLimit : null,
    listone: Boolean(payload.listone),
    fantacalcioLeagueSlug: sanitizeLeagueSlug(payload.fantacalcioLeagueSlug),
    ownerUid: uid,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    status: 'active',
  };

  const ref = await addDoc(auctionBasePath(uid), auctionPayload);

  if (auctionPayload.visibility === 'public') {
    syncPublicAuction(uid, ref.id, auctionPayload).catch(() => {});
  }
  syncUserAuctionCount(uid).catch(() => {});

  return ref.id;
}

export async function updateAuction(uid, auctionId, payload) {
  await setDoc(
    auctionDocPath(uid, auctionId),
    {
      ...payload,
      updatedAt: Date.now(),
    },
    { merge: true }
  );

  return true;
}

export async function deleteAuction(uid, auctionId) {
  if (!uid || !auctionId) return false;

  const purchasesSnap = await getDocs(purchasesCollectionPath(uid, auctionId));
  const notesSnap = await getDocs(playerNotesCollectionPath(uid, auctionId));
  const batch = writeBatch(db);

  purchasesSnap.docs.forEach((purchaseDoc) => {
    batch.delete(purchaseDoc.ref);
  });

  notesSnap.docs.forEach((noteDoc) => {
    batch.delete(noteDoc.ref);
  });

  batch.delete(auctionDocPath(uid, auctionId));
  await batch.commit();

  // Rimuove il documento pubblico se l'asta era stata pubblicata
  await removePublicAuction(uid, auctionId);
  syncUserAuctionCount(uid).catch(() => {});

  return true;
}

export async function getAuctionById(uid, auctionId) {
  const snap = await getDoc(auctionDocPath(uid, auctionId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function getAuctionPurchases(uid, auctionId) {
  const q = query(purchasesCollectionPath(uid, auctionId), orderBy('acquiredAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export function subscribeToAuctionPurchases(uid, auctionId, callback) {
  const q = query(purchasesCollectionPath(uid, auctionId), orderBy('acquiredAt', 'desc'));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// Selezione corrente nel tab Mercato (giocatore + crediti impostati), per
// mostrarla dal vivo nella visualizzazione compatta del tab Squadre — anche
// in un'ALTRA scheda/dispositivo (proprio account su un'altra scheda, o un
// pubblico che segue un'asta pubblica). Campo separato sul documento asta
// (non un merge tramite updateAuction) apposta per non toccare `updatedAt`
// a ogni tasto premuto sui crediti: quel campo è usato altrove per l'ordine
// "ultima modifica" delle aste, che non deve muoversi per questo.
export async function updateAuctionLiveSelection(uid, auctionId, selection) {
  if (!uid || !auctionId) return false;
  await setDoc(auctionDocPath(uid, auctionId), { liveSelection: selection || null }, { merge: true });
  return true;
}

export function subscribeToAuctionLiveSelection(uid, auctionId, callback) {
  if (!uid || !auctionId) return () => {};
  return onSnapshot(auctionDocPath(uid, auctionId), (snap) => {
    callback(snap.exists() ? (snap.data()?.liveSelection || null) : null);
  });
}

export async function getUserAuctionsCount(uid) {
  if (!uid) return 0;

  const snap = await getDocs(auctionBasePath(uid));
  return snap.size;
}

// Conteggio totale delle aste di TUTTI gli utenti (Home, statistica "Aste").
// Le aste private non sono leggibili dagli altri client (vedi firestore.rules),
// quindi un conteggio reale via collectionGroup non è possibile lato client:
// ogni utente riporta invece il proprio numero di aste in un'unica mappa
// condivisa (counts.{uid}) dentro appMeta/auctionCounts, e il totale è la
// somma di quei contatori auto-riportati.
const AUCTION_COUNTS_DOC_PATH = ['appMeta', 'auctionCounts'];

export async function syncUserAuctionCount(uid) {
  if (!uid) return 0;

  const count = await getUserAuctionsCount(uid);
  await setDoc(doc(db, ...AUCTION_COUNTS_DOC_PATH), { counts: { [uid]: count } }, { merge: true });
  return count;
}

export async function getTotalAuctionsCount() {
  const snap = await getDoc(doc(db, ...AUCTION_COUNTS_DOC_PATH));
  const counts = snap.exists() ? snap.data().counts || {} : {};
  return Object.values(counts).reduce((sum, n) => sum + Number(n || 0), 0);
}

export async function getMarketPmaByPlayerId(uid, currentAuction) {
  if (!uid || !currentAuction?.id) return {};

  const allAuctions = await getUserAuctions(uid).catch(() => []);
  const relevantAuctions = allAuctions.filter(
    (auction) => auction.id !== currentAuction.id && sameAuctionConfig(auction, currentAuction)
  );

  if (!relevantAuctions.length) return {};

  const purchasesPerAuction = await Promise.all(
    relevantAuctions.map(async (auction) => {
      const purchases = await getAuctionPurchases(uid, auction.id).catch(() => []);
      return purchases.filter((purchase) => !purchase.deleted);
    })
  );

  const grouped = new Map();

  purchasesPerAuction.flat().forEach((purchase) => {
    const playerId = String(purchase.playerId);
    const credits = Number(purchase.credits);

    if (!playerId || !Number.isFinite(credits)) return;

    if (!grouped.has(playerId)) grouped.set(playerId, []);
    grouped.get(playerId).push(credits);
  });

  const result = {};

  grouped.forEach((values, playerId) => {
    if (!values.length) return;

    const average = values.reduce((sum, value) => sum + value, 0) / values.length;

    result[playerId] = {
      average: Number(average.toFixed(2)),
      samples: values.length,
    };
  });

  return result;
}

// Versione V2: legge da pmaStats (collection top-level, singolo where indicizzato
// su rolesMode — indice singolo automatico, non richiede indici compositi).
// Esclude i propri acquisti dell'asta corrente.
//
// Match cross-asta solo sulla modalità ruoli (classic/mantra): sorgente
// (FantApp/FantaMaster) e numero di partecipanti non contano più, i crediti
// totali dell'asta nemmeno — ogni acquisto viene normalizzato come quota
// percentuale del budget della SUA asta e riscalato sul budget dell'asta
// corrente, così un prezzo pagato in un'asta da 500 crediti resta
// confrontabile con uno pagato in un'asta da 1000 (es. Barella pagato 20/500
// = 4% del budget; visto in un'asta da 1000 crediti pesa 40, il 4% di 1000).
// Mescolare rolesMode diversi non avrebbe invece senso: i ruoli — e quindi i
// prezzi di mercato — non sono comparabili tra Classic e Mantra.
export async function getMarketPmaByPlayerIdV2(currentAuction) {
  if (!currentAuction?.id) return {};

  const mode = getAuctionRolesMode(currentAuction);
  const targetCredits = Number(currentAuction.credits || 0);
  if (!targetCredits) return {};

  const currentAuctionId = currentAuction.id;

  const q = query(
    collection(db, PMA_STATS_COLLECTION),
    where('rolesMode', '==', mode)
  );

  const snap = await getDocs(q);

  const grouped = new Map();

  snap.docs.forEach((d) => {
    const data = d.data();
    // Esclude i record dell'asta corrente
    if (data.auctionId === currentAuctionId) return;

    const pid = String(data.playerId || '');
    const paidCredits = Number(data.credits || 0);
    const sourceCredits = Number(data.auctionCredits || 0);
    if (!pid || paidCredits <= 0 || sourceCredits <= 0) return;

    const normalizedCredits = (paidCredits / sourceCredits) * targetCredits;

    if (!grouped.has(pid)) grouped.set(pid, []);
    grouped.get(pid).push(normalizedCredits);
  });

  const result = {};
  grouped.forEach((values, playerId) => {
    result[playerId] = {
      average: Number(trimmedAverage(values).toFixed(2)),
      samples: values.length,
    };
  });

  return result;
}

// Media troncata: con almeno 4 campioni scarta il valore più basso e quello
// più alto prima di fare la media, per attutire il singolo acquisto anomalo
// (last-minute panic buy, affare fuori scala) senza buttare via il resto del
// campione. Sotto i 4 campioni togliere min e max lascerebbe 1 solo valore
// (con 3) o niente (con 2/1) — non sarebbe più una media, quindi si usa la
// media semplice su tutti i valori.
function trimmedAverage(values) {
  if (values.length < 4) {
    return values.reduce((s, c) => s + c, 0) / values.length;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const trimmed = sorted.slice(1, -1);
  return trimmed.reduce((s, c) => s + c, 0) / trimmed.length;
}

export async function getMarketPmaByPlayerIdForAuction(auction) {
  if (!auction?.id) return {};

  const limits = normalizeRosterLimits(auction.rosterLimits || DEFAULT_ROSTER_LIMITS);
  const mode = getAuctionRolesMode(auction);
  const currentAuctionPath = auction.ownerUid ? `users/${auction.ownerUid}/auctions/${auction.id}` : null;

  const auctionsQuery = query(
    collectionGroup(db, AUCTIONS_COLLECTION),
    where('rolesMode', '==', mode),
    where('participantsCount', '==', Number(auction.participantsCount || 0)),
    where('credits', '==', Number(auction.credits || 0)),
    where('rosterLimits.P', '==', limits.P),
    where('rosterLimits.D', '==', limits.D),
    where('rosterLimits.C', '==', limits.C),
    where('rosterLimits.A', '==', limits.A)
  );

  const auctionsSnap = await getDocs(auctionsQuery);

  const sums = new Map();
  const counts = new Map();

  for (const auctionDoc of auctionsSnap.docs) {
    if (currentAuctionPath && auctionDoc.ref.path === currentAuctionPath) continue;

    const purchasesSnap = await getDocs(collection(auctionDoc.ref, PURCHASES_COLLECTION));

    purchasesSnap.docs.forEach((purchaseDoc) => {
      const purchase = purchaseDoc.data();
      if (purchase?.deleted) return;

      const playerId = String(purchase.playerId || purchaseDoc.id || '');
      const spent = Number(purchase.credits || 0);

      if (!playerId || !Number.isFinite(spent) || spent <= 0) return;

      sums.set(playerId, (sums.get(playerId) || 0) + spent);
      counts.set(playerId, (counts.get(playerId) || 0) + 1);
    });
  }

  const result = {};

  sums.forEach((total, playerId) => {
    const count = counts.get(playerId) || 0;
    result[playerId] = {
      total,
      count,
      average: count ? total / count : null,
    };
  });

  return result;
}

export async function savePurchaseValuation(uid, auctionId, purchaseId, valuation) {
  if (!uid || !auctionId || !purchaseId) return false;

  const value = Number(valuation);
  if (!Number.isFinite(value)) return false;

  const ref = doc(
    db,
    USERS_COLLECTION,
    uid,
    AUCTIONS_COLLECTION,
    auctionId,
    PURCHASES_COLLECTION,
    String(purchaseId)
  );

  await setDoc(
    ref,
    {
      valuation: value,
      valuationUpdatedAt: Date.now(),
      playerSnapshot: {
        value,
      },
      updatedAt: Date.now(),
    },
    { merge: true }
  );

  await setDoc(
    auctionDocPath(uid, auctionId),
    { updatedAt: Date.now() },
    { merge: true }
  );

  return true;
}

export async function savePurchase(uid, auctionId, purchase, auctionConfig = null) {
  const { playerId } = purchase;
  const credits = Number(purchase.credits || 0);
  const now = Date.now();

  // Batch: purchase + aggiornamento timestamp asta in un'unica roundtrip
  const batch = writeBatch(db);

  batch.set(
    doc(db, USERS_COLLECTION, uid, AUCTIONS_COLLECTION, auctionId, PURCHASES_COLLECTION, String(playerId)),
    { ...purchase, playerId: String(playerId), deleted: false, deletedAt: null, updatedAt: now },
    { merge: true }
  );
  batch.set(auctionDocPath(uid, auctionId), { updatedAt: now }, { merge: true });

  await batch.commit();

  // pmaStats: fire-and-forget, non blocca il flusso acquisto. auctionCredits
  // (budget totale di QUESTA asta) è necessario per normalizzare il prezzo
  // quando verrà confrontato con aste di taglio diverso (vedi
  // getMarketPmaByPlayerIdV2).
  const auctionCredits = Number(auctionConfig?.credits || 0);
  if (auctionConfig && credits > 0 && auctionCredits > 0) {
    const statId = pmaStatDocId(uid, auctionId, playerId);
    setDoc(doc(db, PMA_STATS_COLLECTION, statId), {
      rolesMode: getAuctionRolesMode(auctionConfig),
      auctionCredits,
      playerId: String(playerId),
      credits,
      uid,
      auctionId,
      updatedAt: now,
    }).catch(() => {});
  }

  return true;
}

export async function deletePurchase(uid, auctionId, playerId) {
  const now = Date.now();

  // Batch: soft-delete + timestamp asta
  const batch = writeBatch(db);
  batch.set(
    doc(db, USERS_COLLECTION, uid, AUCTIONS_COLLECTION, auctionId, PURCHASES_COLLECTION, String(playerId)),
    { deleted: true, deletedAt: now, updatedAt: now },
    { merge: true }
  );
  batch.set(auctionDocPath(uid, auctionId), { updatedAt: now }, { merge: true });

  await batch.commit();

  // pmaStats: fire-and-forget
  deleteDoc(doc(db, PMA_STATS_COLLECTION, pmaStatDocId(uid, auctionId, playerId))).catch(() => {});

  return true;
}

// Come deletePurchase ma registra anche quanti crediti si recuperano
// rimuovendo il giocatore (usato dalle aste a listone: la differenza
// pagato − recuperato resta a carico del budget). L'acquisto NON viene
// eliminato dal PMA cross-asta come fa deletePurchase — resta uno "storico"
// utile solo alla propria economia listone.
export async function deletePurchaseWithRecovery(uid, auctionId, playerId, creditsRecovered) {
  const now = Date.now();
  const recovered = Math.max(0, Number(creditsRecovered) || 0);

  const batch = writeBatch(db);
  batch.set(
    doc(db, USERS_COLLECTION, uid, AUCTIONS_COLLECTION, auctionId, PURCHASES_COLLECTION, String(playerId)),
    { deleted: true, deletedAt: now, updatedAt: now, creditsRecovered: recovered },
    { merge: true }
  );
  batch.set(auctionDocPath(uid, auctionId), { updatedAt: now }, { merge: true });

  await batch.commit();

  deleteDoc(doc(db, PMA_STATS_COLLECTION, pmaStatDocId(uid, auctionId, playerId))).catch(() => {});

  return true;
}

// Backfill: scrive in pmaStats tutti gli acquisti esistenti di un'asta.
// Va chiamato una volta per asta (es. al caricamento). Idempotente.
export async function backfillPmaStats(uid, auctionId, purchases, auctionConfig) {
  if (!uid || !auctionId || !purchases?.length || !auctionConfig) return;
  const rolesMode = getAuctionRolesMode(auctionConfig);
  const auctionCredits = Number(auctionConfig.credits || 0);
  if (!auctionCredits) return;
  const batch = writeBatch(db);
  let count = 0;

  for (const p of purchases) {
    const pid = String(p.playerId || '');
    const credits = Number(p.credits || 0);
    if (!pid || credits <= 0 || p.deleted) continue;

    batch.set(doc(db, PMA_STATS_COLLECTION, pmaStatDocId(uid, auctionId, pid)), {
      rolesMode,
      auctionCredits,
      playerId: pid,
      credits,
      uid,
      auctionId,
      updatedAt: Date.now(),
    });
    count++;
    // Firestore batch max 500 ops — flush e ricomincia
    if (count % 490 === 0) {
      await batch.commit();
    }
  }

  if (count % 490 !== 0) await batch.commit().catch(() => {});
}

// Migrazione una tantum: backfillPmaStats normale gira solo per l'asta che
// si sta aprendo, quindi le aste vecchie mai più riaperte restano bloccate
// sul vecchio schema pmaStats (configKey) e non contribuiscono al PMA di
// nessuno finché qualcuno non le riapre — un problema del tipo "uovo e
// gallina" quando si introduce un nuovo schema. Questa funzione scorre TUTTE
// le aste dell'utente in un colpo solo e le migra tutte, così il PMA ha
// subito dati reali invece di dover aspettare che ogni singola asta venga
// riaperta una per una. Va chiamata una tantum (es. da AuctionsPage con un
// flag in localStorage) — idempotente, si può richiamare più volte senza
// effetti collaterali.
export async function backfillAllPmaStats(uid) {
  if (!uid) return;
  const auctions = await getUserAuctions(uid).catch(() => []);

  for (const auction of auctions) {
    if (!Number(auction.credits)) continue;
    const purchases = await getAuctionPurchases(uid, auction.id).catch(() => []);
    if (!purchases.length) continue;
    await backfillPmaStats(uid, auction.id, purchases, auction).catch(() => {});
  }
}